/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { DvtTextStyle } from '../../utils/UNSAFE_visUtils';
import { AxisPosition, Dimension } from '../axis.types';
import { AXIS_DEFAULTS } from './axisDefaults';
import type { useTextDimensions } from '../../hooks/PRIVATE_useTextDimensions';
type GetTextDimensions = NonNullable<ReturnType<typeof useTextDimensions>['getTextDimensions']>;

/**
 * Returns the dimension of the title of the chart.
 * @param getTextDimensions
 * @param availSpace
 * @param isHoriz
 * @param title
 * @param titleStyle
 * @returns
 */

export function getTitleDimensions(
  title: string | undefined,
  titleStyle: DvtTextStyle | undefined,
  axisMaxDim: number,
  isHoriz: boolean,
  getTextDimensions: GetTextDimensions
) {
  if (!title || !getTextDimensions) {
    return { width: 0, height: 0 };
  }
  const { width, height } = getTextDimensions(title, titleStyle);

  const titleWidth = isHoriz ? width : height + AXIS_DEFAULTS.titleGap;
  const titleHeight = isHoriz ? height + AXIS_DEFAULTS.titleGap : width;

  if ((!isHoriz && titleWidth > axisMaxDim) || (isHoriz && titleHeight > axisMaxDim)) {
    // title does not fit.
    return { width: 0, height: 0 };
  }
  return { width: isHoriz ? width : titleHeight, height: isHoriz ? titleHeight : titleWidth };
}

/**
 * Returns the (x, y) coordinate of the text given the width and height of the chart.
 * @param position
 * @param availSpace
 * @param textDims
 * @returns
 */

export const getTitlePos = (
  position: AxisPosition,
  availSpace: Dimension,
  textDims: { width: number; height: number }
) => {
  let x: number;
  let y: number;

  if (position === 'left') {
    x = availSpace.x + textDims.height / 2;
    y = availSpace.y + availSpace.height / 2 + textDims.width / 2;
  } else if (position === 'right') {
    x = availSpace.x + availSpace.width - textDims.height / 2;
    y = availSpace.y + availSpace.height / 2 + textDims.width / 2;
  } else if (position === 'top') {
    x = availSpace.x + availSpace.width / 2 - textDims.width / 2;
    y = availSpace.y;
  } else {
    x = availSpace.x + availSpace.width / 2 - textDims.width / 2;
    y = availSpace.y + availSpace.height;
  }
  return { x, y };
};

/**
 * Returns the title props.
 * @param titleStyle
 * @param availSpace
 * @param dims
 * @param position
 * @returns
 */
export function getTitleProps(
  title: string | undefined,
  titleStyle: Record<string, any>,
  maxAxisDims: number,
  availSpace: Dimension,
  position: AxisPosition,
  getTextDimensions?: GetTextDimensions
) {
  if (!getTextDimensions) {
    return {
      width: 0,
      height: 0
    };
  }
  const isHoriz = position === 'top' || position === 'bottom';
  const dims = getTitleDimensions(title, titleStyle, maxAxisDims, isHoriz, getTextDimensions);
  if (dims.width === 0 || dims.height === 0) {
    return dims;
  }
  const { x, y } = getTitlePos(position, availSpace, dims);
  const isRotated = position === 'left' || position === 'right';
  const transform = isRotated ? `matrix(0, -1, 1, 0, ${x}, ${y})` : undefined;
  const titleDims = dims.height;

  if (isRotated) {
    availSpace.width -= titleDims;
    availSpace.x += position === 'left' ? titleDims : 0;
  } else {
    availSpace.height -= titleDims;
    availSpace.y += position === 'top' ? titleDims : 0;
  }

  return {
    ...dims,
    x: isRotated ? undefined : x,
    y: isRotated ? undefined : y,
    transform: isRotated ? transform : undefined,
    style: { fill: titleStyle['color'], ...titleStyle }
  };
}
